import { useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import styled from 'styled-components';
import axios from 'axios';
import { useParams, useNavigate } from 'react-router-dom';
import { FaArrowLeft, FaEdit, FaSave, FaTimes, FaTrash, FaImage, FaCopy } from 'react-icons/fa';
import { successAlert, errorAlert } from '../utils/Alerts';
import PhoneInput from 'react-phone-number-input';
import labels from 'react-phone-number-input/locale/es.json';
import 'react-phone-number-input/style.css';
import ProductDetailView from './ProductDetailView';


import downloadIcon from '../assets/img/descarga.png';
import docxIcon     from '../assets/img/docx.png';
import pdfIcon      from '../assets/img/pdf.png';
import txtIcon      from '../assets/img/txt.png';
import videoIcon    from '../assets/img/video-descarga.png';
import musicaIcon   from '../assets/img/musica.png';
import MediaPlayer  from './MediaPlayer';
import { parsePhoneNumberFromString } from 'libphonenumber-js';

// ══════════════════════════════════════════════════════
//  HELPER: convierte un nombre en URL (slug)
// ══════════════════════════════════════════════════════
const slugify = (text) =>
  text
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')   // quita tildes: Edelí → Edeli
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')       // espacios y símbolos → guion
    .replace(/^-+|-+$/g, '');          // quita guiones al inicio/final

// ══════════════════════════════════════════════════════
//  MULTISELECT DROPDOWN — portal al body, posicion calculada del trigger
// ══════════════════════════════════════════════════════
const MultiSelectDropdown = ({ label, emoji, color, users, selectedIds, onToggle, isDark }) => {
  const [open, setOpen]     = useState(false);
  const [search, setSearch] = useState('');
  const [coords, setCoords] = useState({ top: 0, left: 0, openDown: true });
  const triggerRef = useRef(null);

  // Calcular posicion del trigger ANTES de abrir
  const calcCoords = () => {
    if (!triggerRef.current) return;
    const r = triggerRef.current.getBoundingClientRect();
    const panelH = 270;
    const panelW = 224;
    const goDown = (window.innerHeight - r.bottom) >= panelH;
    setCoords({
      top:      goDown ? r.bottom + 4 : r.top - panelH - 4,
      left:     Math.max(4, Math.min(r.left - panelW + r.width, window.innerWidth - panelW - 4)),
      openDown: goDown,
    });
  };

  const handleClick = (e) => {
    e.stopPropagation();
    if (!open) { calcCoords(); }
    setOpen(o => !o);
    if (open) setSearch('');
  };

  // Cerrar al click fuera
  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (triggerRef.current && triggerRef.current.contains(e.target)) return;
      setOpen(false);
      setSearch('');
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const filtered = users.filter(u =>
    u.username.toLowerCase().includes(search.toLowerCase())
  );
  const count = selectedIds.length;

  const panel = (
    <DropPortal
      isDark={isDark}
      style={{ top: coords.top, left: coords.left }}
      onMouseDown={e => e.stopPropagation()}
    >
      <DropHeader isDark={isDark}>
        <DropTitle isDark={isDark}>{label}</DropTitle>
        <DropSearch
          isDark={isDark}
          placeholder="Buscar usuario..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          autoFocus
        />
      </DropHeader>
      <DropList>
        {filtered.length === 0 ? (
          <DropEmpty isDark={isDark}>Sin resultados</DropEmpty>
        ) : filtered.map(u => {
          const isSel = selectedIds.includes(String(u._id));
          return (
            <DropItem
              key={u._id}
              isDark={isDark}
              selected={isSel}
              color={color}
              onMouseDown={(e) => { e.stopPropagation(); onToggle(u._id, isSel); }}
            >
              <DropAvatar color={color}>{u.username.charAt(0).toUpperCase()}</DropAvatar>
              <DropItemName isDark={isDark}>{u.username}</DropItemName>
              <DropCheck selected={isSel} color={color}>{isSel ? '✓' : ''}</DropCheck>
            </DropItem>
          );
        })}
      </DropList>
    </DropPortal>
  );

  return (
    <>
      <DropTrigger
        ref={triggerRef}
        color={color}
        hasItems={count > 0}
        onClick={handleClick}
        title={label}
      >
        <span>{emoji}</span>
        {count > 0 && <DropBadge color={color}>{count}</DropBadge>}
      </DropTrigger>

      {open && ReactDOM.createPortal(panel, document.body)}
    </>
  );
};

const UserProfile = ({ token, theme, userId, currentUserId, currentUserRole, onUsernameUpdate }) => {
  const params   = useParams();
  const id       = params.id || userId;
  const navigate = useNavigate();
  const isDark   = theme === 'dark';
  const { slug } = params;
  const basePath = slug ? `/creador/${slug}` : '/dashboard';

  const [user,            setUser]            = useState(null);
  const [contents,        setContents]        = useState([]);
  const [images,          setImages]          = useState({});
  const [loading,         setLoading]         = useState(true);
  const [editingInfo,     setEditingInfo]     = useState(false);
  const [saving,          setSaving]          = useState(false);
  const [activeTab, setActiveTab] = useState(
    (currentUserRole === 'Admin' || String(currentUserId) === String(userId)) ? 'info' : 'contents'
  );
  const [selectedContent, setSelectedContent] = useState(null);
  const [allUsers,        setAllUsers]        = useState([]);

  const [phone,      setPhone]      = useState('');
  const [countryISO, setCountryISO] = useState('VE');

  const [editForm, setEditForm] = useState({
    username: '', email: '', document: '', role: '',
    nombre: '', apellido: ''
  });

  const [editingStore, setEditingStore] = useState(false);
  const [savingStore,  setSavingStore]  = useState(false);
  const [logoFile,     setLogoFile]     = useState(null);
  const [logoPreview,  setLogoPreview]  = useState('');
  const [dragOverLogo, setDragOverLogo] = useState(false);
  const [storeForm, setStoreForm] = useState({ storeName: '', storeSlug: '', storeRif: '', storeAddress: '' });
  const [storeTouched, setStoreTouched] = useState({});

  const validateStore = (f) => {
    const e = {};
    const name = f.storeName.trim();
    const rif  = f.storeRif.trim();
    const slug = f.storeSlug.trim();

    if (!name) e.storeName = 'El nombre de la tienda es obligatorio';
    else if (name.length < 3) e.storeName = 'Debe tener al menos 3 caracteres';

    if (rif && !/^[JGVEPC]-?\d{5,9}(-?\d)?$/i.test(rif))
      e.storeRif = 'Formato inválido. Ejemplo: J-12345678-9';

    if (!slug) e.storeSlug = 'La URL de la tienda es obligatoria';
    else if (slug.length < 3) e.storeSlug = 'Debe tener al menos 3 caracteres';
    else if (!/^[a-z0-9]+(-[a-z0-9]+)*$/.test(slug))
      e.storeSlug = 'Solo minúsculas, números y guiones. Ejemplo: mi-tienda';

    return e;
  };

  const storeErrors = validateStore(storeForm);
  const showErr = (field) => storeTouched[field] && storeErrors[field];

  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 8;

  useEffect(() => { fetchUserData(); }, [id]); // eslint-disable-line

  const fetchUserData = async () => {
    try {
      setLoading(true);
      const [userRes, contentsRes, usersRes] = await Promise.all([
        axios.get(`${process.env.REACT_APP_API_URL}/api/admin/users/${id}`,          { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${process.env.REACT_APP_API_URL}/api/admin/users/${id}/contents`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${process.env.REACT_APP_API_URL}/api/user/assignable-users`,      { headers: { Authorization: `Bearer ${token}` } }),
      ]);
      setUser(userRes.data);
      setStoreForm({
        storeName: userRes.data.storeName || '',
        storeSlug: userRes.data.storeSlug || '',
        storeRif: userRes.data.storeRif || '',
        storeAddress: userRes.data.storeAddress || '',
      });
      setLogoPreview(userRes.data.storeLogo || '');
      setEditForm({
        username: userRes.data.username || '', email:    userRes.data.email    || '',
        document: userRes.data.document || '', role:     userRes.data.role     || 'Usuario',
        nombre:   userRes.data.nombre   || '', apellido: userRes.data.apellido || '',
      });
      setPhone(`${userRes.data.codeCountry || ''}${userRes.data.celular || ''}`);
      setCountryISO(userRes.data.countryISO || 'VE');
      setContents(contentsRes.data);
      setAllUsers(usersRes.data || []);
    } catch (err) {
      console.error('Error fetching user data:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!contents.length) return;
    const load = async () => {
      for (const c of contents) {
        if (c.type === 'imagen' || c.isDigitalProduct) {
          try {
            const res = await axios.get(`${process.env.REACT_APP_API_URL}/api/user/img-content/${c._id}`);
            setImages(prev => ({ ...prev, [c._id]: res.data.image }));
          } catch { /* silencioso */ }
        }
      }
    };
    load();
  }, [contents]);

  const downloadFile = async (contentId) => {
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/user/download-content/${contentId}`,
        { responseType: 'blob', headers: { Authorization: `Bearer ${token}` } }
      );
      const url  = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href  = url;
      const ct   = res.headers['content-type'];
      link.download = `archivo_${contentId}${ct.includes('video') ? '.mp4' : ct.includes('audio') ? '.mp3' : ct.includes('pdf') ? '.pdf' : '.docx'}`;
      document.body.appendChild(link); link.click(); document.body.removeChild(link);
    } catch { alert('Error al descargar'); }
  };

  const handleEditChange = e => setEditForm(p => ({ ...p, [e.target.name]: e.target.value }));

  const isOwnProfile = String(currentUserId) === String(id);

  const handleSaveStore = async () => {
    setStoreTouched({ storeName: true, storeRif: true, storeSlug: true });
    if (Object.keys(validateStore(storeForm)).length > 0) return;
    setSavingStore(true);
    try {
      let res;

      if (isOwnProfile) {
        // El creador edita su propia tienda: nombre + slug + logo en un solo PATCH
        const fd = new FormData();
        fd.append('storeName', storeForm.storeName);
        fd.append('storeSlug', storeForm.storeSlug);
        fd.append('storeRif', storeForm.storeRif);
        fd.append('storeAddress', storeForm.storeAddress);
        if (logoFile) fd.append('logo', logoFile);

        res = await axios.patch(
          `${process.env.REACT_APP_API_URL}/api/user/store`,
          fd,
          { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' } }
        );
        setUser(prev => ({ ...prev, ...res.data.store }));
      } else {
        // El admin edita nombre/slug de otro creador
        const putRes = await axios.put(
          `${process.env.REACT_APP_API_URL}/api/admin/users/${id}`,
          { storeName: storeForm.storeName, storeSlug: storeForm.storeSlug, storeRif: storeForm.storeRif, storeAddress: storeForm.storeAddress },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        let updatedUser = putRes.data.user;

        // Si el admin también cambió el logo, va en una llamada aparte
        if (logoFile) {
          const fd = new FormData();
          fd.append('logo', logoFile);
          const logoRes = await axios.patch(
            `${process.env.REACT_APP_API_URL}/api/admin/users/${id}/store-logo`,
            fd,
            { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' } }
          );
          updatedUser = { ...updatedUser, storeLogo: logoRes.data.storeLogo };
        }
        setUser(updatedUser);
      }

      setEditingStore(false);
      setLogoFile(null);
      successAlert({ text: 'Tienda actualizada correctamente', timer: 2000 });
    } catch (err) {
      errorAlert({ title: 'Error', text: err?.response?.data?.message || 'No se pudo actualizar la tienda' });
    } finally {
      setSavingStore(false);
    }
  };

  const handleDeleteStore = async () => {
    if (!window.confirm('¿Eliminar la tienda de este creador? Se borrará el logo, nombre, RIF y URL. Esta acción no se puede deshacer.')) return;
    try {
      await axios.delete(
        `${process.env.REACT_APP_API_URL}/api/admin/users/${id}/store`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setUser(prev => ({ ...prev, storeName: '', storeLogo: '', storeSlug: null, storeRif: '' }));
      setStoreForm({ storeName: '', storeSlug: '', storeRif: '', storeAddress: '' });
      setLogoPreview('');
      successAlert({ text: 'Tienda eliminada correctamente', timer: 2000 });
    } catch (err) {
      errorAlert({ title: 'Error', text: err?.response?.data?.message || 'No se pudo eliminar la tienda' });
    }
  };

  const handleLogoChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setLogoFile(file);
    setLogoPreview(URL.createObjectURL(file));
  };

  const handleSaveInfo = async () => {
    setSaving(true);
    try {
      const phoneNumber = parsePhoneNumberFromString(phone);
      const localNumber = phoneNumber ? phoneNumber.nationalNumber : phone;
      const dialCode = phoneNumber ? `+${phoneNumber.countryCallingCode}` : '';

      const res = await axios.put(
        `${process.env.REACT_APP_API_URL}/api/admin/users/${id}`,
        // CAMBIO AQUÍ: Envía localNumber y codeCountry en lugar de phone
        { ...editForm, celular: localNumber, codeCountry: dialCode, countryISO },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      // Actualizar estado local
      setUser(res.data.user || { ...user, ...editForm, celular: localNumber, codeCountry: dialCode });
      setEditingInfo(false);

      if (id === currentUserId) {
        if (res.data.token) localStorage.setItem('token', res.data.token);
        if (onUsernameUpdate) onUsernameUpdate(editForm.username);
      }
      successAlert({ text: 'Datos actualizados correctamente', timer: 2000 });
    } catch (err) {
      errorAlert({ title: 'Error', text: err?.response?.data?.message || 'No se pudo actualizar' });
    } finally { setSaving(false); }
  };

  const handleDeleteContent = async (contentId) => {
    if (!window.confirm('¿Eliminar este contenido?')) return;
    try {
      await axios.delete(
        `${process.env.REACT_APP_API_URL}/api/user/content/${contentId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setContents(prev => prev.filter(c => c._id !== contentId));
      successAlert({ text: 'Contenido eliminado', timer: 2000 });
    } catch { errorAlert({ title: 'Error', text: 'No se pudo eliminar el contenido' }); }
  };

  const canDownload = (content) => {
    if (content.type === 'video') return false;
    if (!content.isDigitalProduct) return true;

    // Creador siempre puede
    const isCreator = String(content.userId?._id || content.userId) === String(currentUserId);
    if (isCreator) return true;

    // Si fue revocado explícitamente → no puede
    const isRevoked = Array.isArray(content.revokedUsers) &&
      content.revokedUsers.some(u => String(u._id || u) === String(currentUserId));
    if (isRevoked) return false;

    // Asignado → puede por defecto
    const isAssigned = Array.isArray(content.assignedTo) &&
      content.assignedTo.some(a => String(a._id || a) === String(currentUserId));
    if (isAssigned) return true;

    // Otros → solo si fue autorizado explícitamente
    return Array.isArray(content.downloadAllowedUsers) &&
      content.downloadAllowedUsers.some(u => String(u._id || u) === String(currentUserId));
  };

  // 🔒 toggle descarga individual por usuario
  const toggleDownloadAccess = async (contentId, targetUserId, isCurrentlyRevoked) => {
    try {
      // si estaba revocado → quitar revocación (allow); si no → revocar
      const endpoint = isCurrentlyRevoked ? 'allow-download' : 'revoke-download';
      await axios.patch(
        `${process.env.REACT_APP_API_URL}/api/user/content/${contentId}/${endpoint}`,
        { userId: targetUserId },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setContents(prev => prev.map(c => {
        if (c._id !== contentId) return c;
        const revoked = c.revokedUsers || [];
        const allowed = c.downloadAllowedUsers || [];
        if (isCurrentlyRevoked) {
          // quitar de revocados, agregar a permitidos
          return {
            ...c,
            revokedUsers:         revoked.filter(u => String(u._id || u) !== String(targetUserId)),
            downloadAllowedUsers: [...allowed, targetUserId],
          };
        } else {
          // agregar a revocados, quitar de permitidos
          return {
            ...c,
            revokedUsers:         [...revoked, targetUserId],
            downloadAllowedUsers: allowed.filter(u => String(u._id || u) !== String(targetUserId)),
          };
        }
      }));
      successAlert({ text: isCurrentlyRevoked ? 'Descarga autorizada' : 'Descarga bloqueada', timer: 1500 });
    } catch {
      errorAlert({ title: 'Error', text: 'No se pudo actualizar el acceso' });
    }
  };

  // 👥 toggle asignación
  const toggleAssign = async (contentId, targetUserId, isCurrentlyAssigned) => {
    try {
      await axios.patch(
        `${process.env.REACT_APP_API_URL}/api/user/content/${contentId}/assign`,
        { userId: targetUserId, action: isCurrentlyAssigned ? 'unassign' : 'assign' },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setContents(prev => prev.map(c => {
        if (c._id !== contentId) return c;
        const assigned = c.assignedTo || [];
        return {
          ...c,
          assignedTo: isCurrentlyAssigned
            ? assigned.filter(a => String(a._id || a) !== String(targetUserId))
            : [...assigned, { _id: targetUserId, username: allUsers.find(u => String(u._id) === String(targetUserId))?.username || '' }],
        };
      }));
      successAlert({ text: isCurrentlyAssigned ? 'Usuario desasignado' : 'Usuario asignado', timer: 1500 });
    } catch {
      errorAlert({ title: 'Error', text: 'No se pudo actualizar la asignacion' });
    }
  };

  const getRoleBadge = role => ({
    Admin:   { bg: 'rgba(99,102,241,0.15)',  color: '#818cf8', border: 'rgba(99,102,241,0.3)' },
    Creador: { bg: 'rgba(34,197,94,0.15)',   color: '#4ade80', border: 'rgba(34,197,94,0.3)' },
  }[role] || { bg: 'rgba(148,163,184,0.15)', color: '#94a3b8', border: 'rgba(148,163,184,0.3)' });

  const formatDate = d => d
    ? new Date(d).toLocaleDateString('es-ES', { day:'2-digit', month:'short', year:'numeric' }) : '—';

  const getFileIcon = file => {
    if (!file) return downloadIcon;
    const f = file.toLowerCase();
    if (f.endsWith('.docx')) return docxIcon;
    if (f.endsWith('.pdf'))  return pdfIcon;
    if (f.endsWith('.txt'))  return txtIcon;
    if (f.endsWith('.mp3'))  return musicaIcon;
    if (f.endsWith('.mp4'))  return videoIcon;
    return downloadIcon;
  };

  const countryToFlag = iso => {
    if (!iso || iso.length !== 2) return '🌍';
    return iso.toUpperCase().replace(/./g, c => String.fromCodePoint(c.charCodeAt(0) + 127397));
  };
  const getCountryName = iso => (iso ? (labels[iso] || iso) : '—');

  const canManageContent = currentUserRole === 'Admin' || String(currentUserId) === String(id);

  const visibleContents = contents.filter(content => {
    if (content.visibility === 'privado') {
      const isOwner    = String(content.userId?._id || content.userId) === String(id);
      const isAssigned = Array.isArray(content.assignedTo) &&
        content.assignedTo.some(a => String(a._id || a) === String(id));
      return isOwner || isAssigned;
    }
    return true;
  });

  const totalPages        = Math.ceil(visibleContents.length / itemsPerPage);
  const paginatedContents = visibleContents.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  if (loading) return <Wrap active={isDark}><Center active={isDark}>Cargando perfil...</Center></Wrap>;
  if (!user)   return <Wrap active={isDark}><Center active={isDark}>Usuario no encontrado.</Center></Wrap>;

  const badge = getRoleBadge(user.role);
  const stats = [
    { label: 'Total',    value: visibleContents.length },
    { label: 'Imágenes', value: visibleContents.filter(c => c.type === 'imagen').length },
    { label: 'Videos',   value: visibleContents.filter(c => c.type === 'video').length },
    { label: 'Archivos', value: visibleContents.filter(c => c.type === 'archivo').length },
  ];

  if (selectedContent) {
    return (
      <ProductDetailView
        content={selectedContent}
        theme={theme}
        token={token}
        images={images}
        userId={id}
        isEmbedded={true}
        onBack={() => setSelectedContent(null)}
      />
    );
  }

  return (
    <Wrap active={isDark}>

      {/* ── TOPBAR: volver | titulo | divider | tabs ── */}
      <TopBar>
        <BackBtn active={isDark} onClick={() => navigate(`${basePath}/users`)}>
          <FaArrowLeft /> Volver
        </BackBtn>
        <PageTitle active={isDark}>Perfil de Usuario</PageTitle>
        <TabsDivider active={isDark} />
        <Tabs>
          {(currentUserRole === 'Admin' || String(currentUserId) === String(id)) && (
            <Tab active={isDark} selected={activeTab === 'info'} onClick={() => setActiveTab('info')}>
              Datos personales
            </Tab>
          )}
          <Tab active={isDark} selected={activeTab === 'contents'} onClick={() => setActiveTab('contents')}>
            Contenidos <TabBadge>{contents.length}</TabBadge>
          </Tab>
        </Tabs>
      </TopBar>

      <Layout>
        {/* ══ COLUMNA IZQUIERDA ══ */}
        <LeftCol>
          <Card active={isDark}>
            <Avatar active={isDark}>{user.username?.charAt(0).toUpperCase()}</Avatar>
            <UserName active={isDark}>
              {user.nombre && user.apellido ? `${user.nombre} ${user.apellido}` : user.username}
            </UserName>
            <UserEmail active={isDark}>{user.email}</UserEmail>
            <Badge bg={badge.bg} color={badge.color} border={badge.border}>{user.role}</Badge>
            <JoinDate active={isDark}>Registrado: {formatDate(user.createdAt)}</JoinDate>
          </Card>

          <Card active={isDark}>
            <CardSectionTitle active={isDark}>Estadísticas</CardSectionTitle>
            <StatsGrid>
              {stats.map((s, i) => (
                <StatBox key={i} active={isDark}>
                  <StatNum active={isDark}>{s.value}</StatNum>
                  <StatLbl active={isDark}>{s.label}</StatLbl>
                </StatBox>
              ))}
            </StatsGrid>
          </Card>
        </LeftCol>

        {/* ══ COLUMNA DERECHA ══ */}
        <RightCol>

          {activeTab === 'info' && user.role === 'Creador' && (
            <Card active={isDark}>
              <CardHead>
                <CardSectionTitle active={isDark}>Datos de Tienda</CardSectionTitle>
                {!editingStore && (
                  <BtnRow>
                    {currentUserRole === 'Admin' && user.storeSlug && (
                      <ActionIconBtn active={isDark} danger onClick={handleDeleteStore}>
                        <FaTrash /> Eliminar tienda
                      </ActionIconBtn>
                    )}
                    {canManageContent && (
                      <ActionIconBtn active={isDark} onClick={() => setEditingStore(true)}>
                        <FaEdit /> Editar
                      </ActionIconBtn>
                    )}
                  </BtnRow>
                )}
                {editingStore && (
                  <BtnRow>
                    <ActionIconBtn active={isDark} danger onClick={() => { setEditingStore(false); setLogoFile(null); setStoreTouched({}); setLogoPreview(user.storeLogo || ''); }}>
                      <FaTimes /> Cancelar
                    </ActionIconBtn>
                    <ActionIconBtn active={isDark} success onClick={handleSaveStore} disabled={savingStore || Object.keys(storeErrors).length > 0}>
                      <FaSave /> Guardar
                    </ActionIconBtn>
                  </BtnRow>
                )}
              </CardHead>

              {editingStore ? (
                    <StoreEditLayout>
                      <StoreLogoCol>
                        <FieldLbl active={isDark}>Logo de la tienda</FieldLbl>
                        <LogoDropZone
                          active={isDark}
                          dragOver={dragOverLogo}
                          onDragOver={e => { e.preventDefault(); setDragOverLogo(true); }}
                          onDragLeave={() => setDragOverLogo(false)}
                          onDrop={e => {
                            e.preventDefault();
                            setDragOverLogo(false);
                            const f = e.dataTransfer.files[0];
                            if (f) handleLogoChange({ target: { files: [f] } });
                          }}
                        >
                          {logoPreview ? (
                            <LogoPreviewImg src={logoPreview} alt="logo" />
                          ) : (
                            <LogoDropContent>
                              <LogoDropIcon active={isDark}><FaImage /></LogoDropIcon>
                              <LogoDropText active={isDark}>Arrastra o haz clic para seleccionar</LogoDropText>
                            </LogoDropContent>
                          )}
                          <LogoHiddenInput type="file" accept="image/png,image/jpeg,image/jpg,image/webp" onChange={handleLogoChange} />
                        </LogoDropZone>
                      </StoreLogoCol>

                      <StoreFieldsCol>
                        <TwoColSmall>
                          <FieldGroup>
                            <FieldLbl active={isDark}>Nombre de la tienda *</FieldLbl>
                            <FieldInp
                              active={isDark}
                              placeholder="Ej: Mi Tienda"
                              style={showErr('storeName') ? { borderColor: '#f87171' } : undefined}
                              value={storeForm.storeName}
                              onChange={e => {
                                const name = e.target.value;
                                setStoreForm(p => ({
                                  ...p,
                                  storeName: name,
                                  storeSlug: slugify(name),
                                }));
                              }}
                              onBlur={() => setStoreTouched(p => ({ ...p, storeName: true }))}
                            />
                            {showErr('storeName') && <FieldError>⚠ {storeErrors.storeName}</FieldError>}
                          </FieldGroup>

                          <FieldGroup>
                            <FieldLbl active={isDark}>RIF</FieldLbl>
                            <FieldInp
                              active={isDark}
                              placeholder="J-12345678-9"
                              style={showErr('storeRif') ? { borderColor: '#f87171' } : undefined}
                              value={storeForm.storeRif}
                              onChange={e => setStoreForm(p => ({ ...p, storeRif: e.target.value }))}
                              onBlur={() => setStoreTouched(p => ({ ...p, storeRif: true }))}
                            />
                            {showErr('storeRif') && <FieldError>⚠ {storeErrors.storeRif}</FieldError>}
                          </FieldGroup>

                          <FieldGroup>
                            <FieldLbl active={isDark}>Dirección</FieldLbl>
                            <FieldInp
                              active={isDark}
                              placeholder="Av. Principal, local 5, Ciudad"
                              value={storeForm.storeAddress}
                              onChange={e => setStoreForm(p => ({ ...p, storeAddress: e.target.value }))}
                            />
                          </FieldGroup>

                          <FieldGroup>
                            <FieldLbl active={isDark}>URL de la tienda (automática)</FieldLbl>
                            <UrlPreview active={isDark} hasError={!!showErr('storeSlug')}>
                              <UrlBase active={isDark}>{window.location.origin}/creador/</UrlBase>
                              <UrlSlug active={isDark}>{storeForm.storeSlug || 'tu-tienda'}</UrlSlug>
                            </UrlPreview>
                            {showErr('storeSlug') && <FieldError>⚠ {storeErrors.storeSlug}</FieldError>}
                          </FieldGroup>
                        </TwoColSmall>
                      </StoreFieldsCol>
                    </StoreEditLayout>
                    ) : (
                  <StoreEditLayout>
                    <StoreLogoCol>
                      <FieldLbl active={isDark}>Logo de la tienda</FieldLbl>
                      {user.storeLogo ? (
                        <StoreLogoImg src={user.storeLogo} alt={user.storeName || 'Logo'} />
                      ) : (
                        <StoreLogoPlaceholder active={isDark}><FaImage /></StoreLogoPlaceholder>
                      )}
                    </StoreLogoCol>

                    <StoreFieldsCol>
                      <TwoColSmall>
                        <InfoCardItem active={isDark}>
                          <InfoCardIcon>🏬</InfoCardIcon>
                          <InfoCardText style={{ textAlign: 'left', alignItems: 'flex-start' }}>
                            <InfoCardLabel active={isDark} style={{ textAlign: 'left' }}>Nombre de tienda</InfoCardLabel>
                            <InfoCardValue active={isDark} style={{ textAlign: 'left', display: 'block' }}>
                              {user.storeName || '—'}
                            </InfoCardValue>
                          </InfoCardText>
                        </InfoCardItem>

                        <InfoCardItem active={isDark}>
                          <InfoCardIcon>🪪</InfoCardIcon>
                          <InfoCardText style={{ textAlign: 'left', alignItems: 'flex-start' }}>
                            <InfoCardLabel active={isDark} style={{ textAlign: 'left' }}>RIF</InfoCardLabel>
                            <InfoCardValue active={isDark} style={{ textAlign: 'left', display: 'block' }}>
                              {user.storeRif || '—'}
                            </InfoCardValue>
                          </InfoCardText>
                        </InfoCardItem>
                      </TwoColSmall>

                      <InfoCardItem active={isDark}>
                        <InfoCardIcon>📍</InfoCardIcon>
                        <InfoCardText style={{ textAlign: 'left', alignItems: 'flex-start' }}>
                          <InfoCardLabel active={isDark} style={{ textAlign: 'left' }}>Dirección</InfoCardLabel>
                          <InfoCardValue active={isDark} style={{ whiteSpace: 'normal', textAlign: 'left', display: 'block' }}>
                            {user.storeAddress || '—'}
                          </InfoCardValue>
                        </InfoCardText>
                      </InfoCardItem>

                      <UrlCardItem active={isDark}>
                        <InfoCardIcon>🔗</InfoCardIcon>
                        <InfoCardText style={{ flex: 1, minWidth: 0, textAlign: 'left', alignItems: 'flex-start' }}>
                          <InfoCardLabel active={isDark} style={{ textAlign: 'left' }}>URL de tu tienda</InfoCardLabel>
                          <InfoCardValue active={isDark} style={{ whiteSpace: 'normal', wordBreak: 'break-all', textAlign: 'left', display: 'block' }}>
                            {user.storeSlug ? `${window.location.origin}/creador/${user.storeSlug}` : '—'}
                          </InfoCardValue>
                        </InfoCardText>
                        {user.storeSlug && (
                          <CopyBtn
                            active={isDark}
                            onClick={() => {
                              navigator.clipboard.writeText(`${window.location.origin}/creador/${user.storeSlug}`);
                              successAlert({ text: 'URL copiada al portapapeles', timer: 1500 });
                            }}
                          >
                            <FaCopy /> Copiar
                          </CopyBtn>
                        )}
                      </UrlCardItem>
                    </StoreFieldsCol>
                  </StoreEditLayout>
                )}
                </Card>
              )}

          {/* ── TAB INFO ── */}
          {activeTab === 'info' && (
            <Card active={isDark}>
              <CardHead>
                <CardSectionTitle active={isDark}>Datos Personales</CardSectionTitle>

                {/* 🟢 Cambiamos esto para usar la variable y quitar el error de ESLint */}
                {!editingInfo && canManageContent && (
                  <ActionIconBtn active={isDark} onClick={() => setEditingInfo(true)}>
                    <FaEdit /> Editar
                  </ActionIconBtn>
                )}

                {editingInfo && (
                  <BtnRow>
                    <ActionIconBtn active={isDark} danger onClick={() => setEditingInfo(false)}>
                      <FaTimes /> Cancelar
                    </ActionIconBtn>
                    <ActionIconBtn active={isDark} success onClick={handleSaveInfo} disabled={saving}>
                      <FaSave /> Guardar
                    </ActionIconBtn>
                  </BtnRow>
                )}
              </CardHead>

              {editingInfo ? (
                <FieldsGrid>
                  {[
                    { label: 'Nombre',   name: 'nombre' },
                    { label: 'Apellido', name: 'apellido' },
                    { label: 'Usuario',  name: 'username' },
                    { label: 'Email',    name: 'email', type: 'email' },
                    { label: 'Cédula',   name: 'document' },
                  ].map(f => (
                    <FieldGroup key={f.name}>
                      <FieldLbl active={isDark}>{f.label}</FieldLbl>
                      <FieldInp active={isDark} name={f.name} type={f.type || 'text'} value={editForm[f.name]} onChange={handleEditChange} />
                    </FieldGroup>
                  ))}
                  <FieldGroup>
                    <FieldLbl active={isDark}>Rol</FieldLbl>
                    <FieldSel active={isDark} name="role" value={editForm.role} onChange={handleEditChange}>
                      <option value="Usuario">Usuario</option>
                      <option value="Creador">Creador</option>
                      {/* Solo mostramos la opción Admin si el usuario que está operando ya es Admin */}
                        {currentUserRole === 'Admin' && (
                          <option value="Admin">Admin</option>
                        )}
                    </FieldSel>
                  </FieldGroup>
                  <FieldGroup>
                    <FieldLbl active={isDark}>Teléfono</FieldLbl>
                    <PhoneInputWrapper active={isDark}>
                      <PhoneInput
                        international
                        defaultCountry={countryISO || 'VE'}
                        value={phone}
                        onChange={setPhone}
                        onCountryChange={setCountryISO}
                        placeholder="Número de teléfono"
                        labels={labels}
                        countrySelectProps={{ unicodeFlags: true }}
                      />
                    </PhoneInputWrapper>
                  </FieldGroup>
                </FieldsGrid>
              ) : (
                <InfoCardGrid>
                  {[
                    { label: 'Nombre',   value: user.nombre,   icon: '📝' },
                    { label: 'Apellido', value: user.apellido, icon: '📝' },
                    { label: 'Usuario',  value: user.username, icon: '👤' },
                    { label: 'Email',    value: user.email,    icon: '✉️' },
                    { label: 'Cédula',   value: user.document, icon: '🪪' },
                    // Si el celular ya empieza con '+', lo mostramos directo.
                    // Si no, le pegamos el código de país.
                    {
                      label: 'Teléfono',
                      value: (() => {
                        if (!user.celular) return '—';
                        // Esta función detecta automáticamente el código y formatea con espacios
                        const phoneNumber = parsePhoneNumberFromString(user.celular);
                        return phoneNumber ? phoneNumber.formatInternational() : user.celular;
                      })(),
                      icon: '📱'
                    },
                    { label: 'Rol',      value: user.role,     icon: '🔑' },
                  ].map(f => (
                    <InfoCardItem key={f.label} active={isDark}>
                      <InfoCardIcon>{f.icon}</InfoCardIcon>
                      <InfoCardText>
                        <InfoCardLabel active={isDark}>{f.label}</InfoCardLabel>
                        <InfoCardValue active={isDark}>{f.value || '—'}</InfoCardValue>
                      </InfoCardText>
                    </InfoCardItem>
                  ))}
                  <InfoCardItem active={isDark}>
                    <InfoCardIcon style={{ fontSize: 28 }}>{countryToFlag(user.countryISO)}</InfoCardIcon>
                    <InfoCardText>
                      <InfoCardLabel active={isDark}>País</InfoCardLabel>
                      <InfoCardValue active={isDark}>{getCountryName(user.countryISO)}</InfoCardValue>
                    </InfoCardText>
                  </InfoCardItem>
                </InfoCardGrid>
              )}

              <SecurityNote active={isDark}>
                🔒 Por seguridad, el administrador no puede ver ni cambiar la contraseña de otros usuarios.
              </SecurityNote>
            </Card>
          )}

          {/* ── TAB CONTENIDOS ── */}
          {activeTab === 'contents' && (
            <Card active={isDark}>
              <CardSectionTitle active={isDark} style={{ marginBottom: 20 }}>
                Contenidos del usuario ({contents.length})
              </CardSectionTitle>

              {contents.length === 0 ? (
                <EmptyMsg active={isDark}>Este usuario no tiene contenidos.</EmptyMsg>
              ) : (
                <>
                  <ContentGrid>
                    {paginatedContents.map(c => {
                      const assignedIds = (c.assignedTo   || []).map(a => String(a._id || a));
                      const revokedIds  = (c.revokedUsers || []).map(u => String(u._id || u));

                      // ¿Quién puede VER/USAR los botones?
                      // Solo el Admin o el creador del contenido (currentUserId)
                      const isCurrentUserContentOwner = String(c.userId?._id || c.userId) === String(currentUserId);
                      const canSeeButtons = currentUserRole === 'Admin' || isCurrentUserContentOwner;

                      // El usuario del perfil que estamos viendo: ¿es creador del contenido?
                      const profileUserIsOwner = String(c.userId?._id || c.userId) === String(id);

                      // El usuario del perfil: ¿ya está asignado?
                      const profileUserIsAssigned = assignedIds.includes(String(id));

                      // 👥 visible si: puede ver botones Y el usuario del perfil es el creador del contenido
                      const showAssignBtn = canSeeButtons || profileUserIsOwner;

                      // 🔒 visible si: puede ver botones, es producto digital,
                      //    el usuario del perfil es el creador Y está asignado
                      //    (a asignados se gestiona con 👥)
                      const showLockBtn = canSeeButtons || (c.isDigitalProduct && profileUserIsOwner && !profileUserIsAssigned);

                      return (
                        <ContentCard key={c._id} active={isDark}>

                          {/* ── PREVIEW ── */}
                          {/* ── PREVIEW DENTRO DE ContentCard ── */}
                          <Preview active={isDark} onClick={() => setSelectedContent(c)}>
                            {c.type === 'imagen' && (() => {
                              const fileStr = Array.isArray(c.file) ? c.file[0] : c.file;
                              const fileLower = fileStr ? fileStr.toLowerCase() : '';

                              // MEJORA: Detección de video incluso si el tipo es 'imagen' (común en uploads de iOS)
                              const isVid = fileLower.endsWith('.mp4') || fileLower.endsWith('.mov') || fileLower.endsWith('.webm');
                              const isAud = fileLower.endsWith('.mp3') || fileLower.endsWith('.wav');

                              if (isVid || isAud) {
                                return (
                                  <MediaPlayer
                                    mediaUrl={fileStr}
                                    mediaType={isVid ? 'video' : 'audio'}
                                    isLocalFile={true}
                                    token={token}
                                    id={c._id}
                                  />
                                );
                              }
                              if (images[c._id]) return <PreviewImg src={images[c._id]} alt={c.title} />;
                              return <PreviewPlaceholder active={isDark}>🖼️</PreviewPlaceholder>;
                            })()}

                            {c.type === 'video' && (
                              c.videoUrl ? (
                                <PreviewIframe
                                  src={`https://www.youtube.com/embed/${new URL(c.videoUrl).searchParams.get('v')}`}
                                  title={c.title} allowFullScreen
                                />
                              ) : (
                                // Si es un video subido directamente (tipo video pero sin URL de YT)
                                <MediaPlayer
                                  mediaUrl={Array.isArray(c.file) ? c.file[0] : c.file}
                                  mediaType="video"
                                  isLocalFile={true}
                                  token={token}
                                  id={c._id}
                                />
                              )
                            )}

                            {c.type === 'archivo' && c.file && (() => {
                              if (c.isDigitalProduct && images[c._id])
                                return <PreviewImg src={images[c._id]} alt={c.title} />;

                              const fileStr = Array.isArray(c.file) ? c.file[0] : c.file;
                              if (!fileStr) return null;
                              const fl = fileStr.toLowerCase();

                              // MEJORA: Soporte .mov en tipo archivo
                              const isVideoFile = fl.endsWith('.mp4') || fl.endsWith('.mov') || fl.endsWith('.webm');
                              const isAudioFile = fl.endsWith('.mp3') || fl.endsWith('.wav');

                              return (isVideoFile || isAudioFile)
                                ? <MediaPlayer
                                    mediaUrl={fileStr}
                                    mediaType={isVideoFile ? 'video' : 'audio'}
                                    isLocalFile
                                    token={token}
                                    id={c._id}
                                  />
                                : (
                                  <FileIconWrap onClick={e => { e.stopPropagation(); if (canDownload(c)) downloadFile(c._id); }}
                                    style={{ cursor: canDownload(c) ? 'pointer' : 'not-allowed', opacity: canDownload(c) ? 1 : 0.4 }}
                                  >
                                    <img src={getFileIcon(fileStr)} alt="archivo" style={{ width: 52 }} />
                                    <FileExt active={isDark}>{fileStr.split('.').pop().toUpperCase()}</FileExt>
                                  </FileIconWrap>
                                );
                            })()}
                          </Preview>

                          {/* ── META + ACCIONES ── */}
                          <ContentMeta active={isDark}>
                            <ContentTitle
                              active={isDark}
                              onClick={() => setSelectedContent(c)}
                              onMouseEnter={e => e.target.style.color = '#a855f7'}
                              onMouseLeave={e => e.target.style.color = isDark ? '#e0e0e0' : '#222'}
                            >
                              {c.title || '—'}
                            </ContentTitle>

                            <ContentFooter>
                              <TypePill type={c.type}>
                                {c.type === 'imagen' ? '🖼️' : c.type === 'video' ? '🎬' : '📄'} {c.type}
                              </TypePill>
                              <DateText active={isDark}>{formatDate(c.createdAt)}</DateText>
                            </ContentFooter>

                            <ActionRow>
                              {c.price > 0
                                ? <PricePill>${Number(c.price).toLocaleString('en-US', { minimumFractionDigits: 2 })}</PricePill>
                                : <span />
                              }

                              {canSeeButtons && (
                                <BtnGroup>
                                  {/* 👥 Asignar — oculto si el usuario del perfil es el creador */}
                                  {showAssignBtn && (
                                    <MultiSelectDropdown
                                      label="Asignar usuarios"
                                      emoji="👥"
                                      color="#60a5fa"
                                      users={allUsers}
                                      selectedIds={assignedIds}
                                      onToggle={(uid, isAssigned) => toggleAssign(c._id, uid, isAssigned)}
                                      isDark={isDark}
                                    />
                                  )}

                                  {/* 🔒 Bloquear — solo si NO está asignado y es producto digital */}
                                  {showLockBtn && (
                                    <MultiSelectDropdown
                                      label="Bloquear descarga"
                                      emoji="🔒"
                                      color="#f87171"
                                      users={allUsers}
                                      selectedIds={revokedIds}
                                      onToggle={(uid, isRevoked) => toggleDownloadAccess(c._id, uid, isRevoked)}
                                      isDark={isDark}
                                    />
                                  )}

                                  {/* ✏️ Editar — al lado del eliminar */}
                                  {canSeeButtons && (
                                    <EditBtn onClick={() => navigate(`/dashboard/edit-content/${c._id}`)} title="Editar">
                                      <FaEdit />
                                    </EditBtn>
                                  )}

                                  {/* 🗑️ Eliminar — siempre visible para admin/creador */}
                                  {canSeeButtons && (
                                    <DeleteBtn onClick={() => handleDeleteContent(c._id)} title="Eliminar">
                                      <FaTrash />
                                    </DeleteBtn>
                                  )}
                                </BtnGroup>
                              )}
                            </ActionRow>
                          </ContentMeta>

                        </ContentCard>
                      );
                    })}
                  </ContentGrid>

                  {totalPages > 1 && (
                    <Pagination>
                      <PageBtn active={isDark} disabled={currentPage === 1} onClick={() => setCurrentPage(p => p - 1)}>←</PageBtn>
                      {Array.from({ length: totalPages }, (_, i) => i + 1).map(p => (
                        <PageBtn key={p} active={isDark} isCurrent={p === currentPage} onClick={() => setCurrentPage(p)}>{p}</PageBtn>
                      ))}
                      <PageBtn active={isDark} disabled={currentPage === totalPages} onClick={() => setCurrentPage(p => p + 1)}>→</PageBtn>
                      <PageInfo active={isDark}>
                        {(currentPage - 1) * itemsPerPage + 1}–{Math.min(currentPage * itemsPerPage, contents.length)} de {contents.length}
                      </PageInfo>
                    </Pagination>
                  )}
                </>
              )}
            </Card>
          )}

        </RightCol>
      </Layout>
    </Wrap>
  );
};

/* ═══════════════════════════════════════════════════════
   STYLED COMPONENTS
═══════════════════════════════════════════════════════ */
const Wrap     = styled.div`background-color:${p=>p.active?'#0a0a0a':'#f4f5f7'};min-height:100%;padding:28px 40px;transition:background-color 0.3s;@media(max-width:768px){padding:20px 16px;}`;
const Center   = styled.p`text-align:center;color:${p=>p.active?'#666':'#aaa'};padding:80px;font-size:15px;`;

/* TopBar: volver + titulo + divider + tabs — todo en una fila */
const TopBar = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  margin-bottom: 24px;
  flex-wrap: wrap;
`;

const BackBtn = styled.button`
  display:flex;align-items:center;gap:8px;flex-shrink:0;
  background:none;border:1px solid ${p=>p.active?'#2a2a2a':'#ddd'};
  color:${p=>p.active?'#888':'#666'};padding:8px 16px;border-radius:8px;
  font-size:13px;cursor:pointer;transition:all 0.2s;
  &:hover{border-color:${p=>p.active?'#555':'#aaa'};color:${p=>p.active?'#fff':'#222'};}
`;

const PageTitle = styled.h2`
  font-size:1.3rem;font-weight:700;
  color:${p=>p.active?'#fff':'#111'};margin:0;flex-shrink:0;
`;

/* línea divisoria vertical entre título y tabs */
const TabsDivider = styled.div`
  width: 1px; height: 28px; flex-shrink: 0;
  background: ${p => p.active ? '#2a2a2a' : '#ddd'};
`;

const Tabs  = styled.div`display:flex;gap:6px;`;
const Tab   = styled.button`
  display:flex;align-items:center;gap:7px;
  padding:8px 16px;border-radius:8px;font-size:13px;font-weight:600;cursor:pointer;
  border:1px solid ${p=>p.selected?(p.active?'#444':'#ccc'):(p.active?'#1e1e1e':'#e8e8e8')};
  background:${p=>p.selected?(p.active?'#1e1e1e':'#fff'):'transparent'};
  color:${p=>p.selected?(p.active?'#fff':'#111'):(p.active?'#909090':'#999')};
  transition:all 0.2s;
  box-shadow:${p=>p.selected?(p.active?'0 2px 10px rgba(0,0,0,0.3)':'0 2px 8px rgba(0,0,0,0.08)'):'none'};
`;
const TabBadge = styled.span`background:rgba(128,128,128,0.2);border-radius:20px;padding:2px 7px;font-size:11px;font-weight:700;`;

const Layout  = styled.div`display:grid;grid-template-columns:250px 1fr;gap:20px;align-items:start;@media(max-width:900px){grid-template-columns:1fr;}`;
const LeftCol = styled.div`display:flex;flex-direction:column;gap:16px;`;
const RightCol= styled.div`display:flex;flex-direction:column;gap:16px;`;

const Card = styled.div`
  background:${p=>p.active?'#141414':'#fff'};
  border:1px solid ${p=>p.active?'#222':'#e8e8e8'};
  border-radius:14px;padding:22px;
  box-shadow:0 2px 16px ${p=>p.active?'rgba(0,0,0,0.4)':'rgba(0,0,0,0.06)'};
`;

const Avatar    = styled.div`width:68px;height:68px;border-radius:50%;background:${p=>p.active?'#252525':'#e8e8e8'};color:${p=>p.active?'#fff':'#555'};display:flex;align-items:center;justify-content:center;font-weight:800;font-size:28px;margin:0 auto 10px;`;
const UserName  = styled.h3`text-align:center;font-size:1rem;font-weight:700;color:${p=>p.active?'#fff':'#111'};margin:0 0 4px;`;
const UserEmail = styled.p`text-align:center;font-size:12px;color:${p=>p.active?'#555':'#aaa'};margin:0 0 10px;`;
const JoinDate  = styled.p`text-align:center;font-size:11px;color:${p=>p.active?'#444':'#bbb'};margin:8px 0 0;`;
const Badge     = styled.span`display:block;width:fit-content;margin:0 auto;padding:3px 12px;border-radius:20px;font-size:12px;font-weight:600;background:${p=>p.bg};color:${p=>p.color};border:1px solid ${p=>p.border};`;

const CardSectionTitle = styled.h4`font-size:11px;font-weight:700;text-transform:uppercase;letter-spacing:1.2px;color:${p=>p.active?'#555':'#999'};margin:0;`;
const StatsGrid = styled.div`display:grid;grid-template-columns:1fr 1fr;gap:8px;margin-top:14px;`;
const StatBox   = styled.div`background:${p=>p.active?'#1c1c1c':'#f7f8fa'};border:1px solid ${p=>p.active?'#272727':'#eee'};border-radius:10px;padding:12px;text-align:center;`;
const StatNum   = styled.div`font-size:1.6rem;font-weight:800;color:${p=>p.active?'#fff':'#111'};`;
const StatLbl   = styled.div`font-size:10px;color:${p=>p.active?'#555':'#bbb'};margin-top:3px;text-transform:uppercase;letter-spacing:0.5px;`;

const CardHead = styled.div`display:flex;align-items:center;justify-content:space-between;margin-bottom:18px;`;
const BtnRow   = styled.div`display:flex;gap:8px;`;
const ActionIconBtn = styled.button`display:flex;align-items:center;gap:6px;padding:7px 14px;border-radius:8px;font-size:12px;font-weight:600;cursor:pointer;background:${p=>p.success?'rgba(34,197,94,0.1)':p.danger?'rgba(220,53,69,0.1)':p.active?'rgba(255,255,255,0.05)':'rgba(0,0,0,0.04)'};border:1px solid ${p=>p.success?'rgba(34,197,94,0.3)':p.danger?'rgba(220,53,69,0.3)':p.active?'rgba(255,255,255,0.1)':'rgba(0,0,0,0.1)'};color:${p=>p.success?'#4ade80':p.danger?'#f87171':p.active?'#ccc':'#555'};transition:all 0.2s;&:hover{opacity:0.8;}&:disabled{opacity:0.4;cursor:not-allowed;}`;

const FieldsGrid = styled.div`display:grid;grid-template-columns:1fr 1fr;gap:14px;@media(max-width:600px){grid-template-columns:1fr;}`;
const FieldGroup = styled.div`display:flex;flex-direction:column;gap:5px;`;
const FieldLbl   = styled.label`font-size:11px;font-weight:700;text-transform:uppercase;letter-spacing:0.5px;color:${p=>p.active?'#555':'#aaa'};`;
const FieldInp   = styled.input`background:${p=>p.active?'#1e1e1e':'#f7f8fa'};border:1px solid ${p=>p.active?'#333':'#ddd'};border-radius:8px;padding:8px 12px;font-size:13px;color:${p=>p.active?'#e0e0e0':'#222'};outline:none;width:100%;box-sizing:border-box;&:focus{border-color:rgba(128,128,128,0.6);}`;
const FieldSel   = styled.select`background:${p=>p.active?'#1e1e1e':'#f7f8fa'};border:1px solid ${p=>p.active?'#333':'#ddd'};border-radius:8px;padding:8px 12px;font-size:13px;color:${p=>p.active?'#e0e0e0':'#222'};outline:none;width:100%;cursor:pointer;`;

const PhoneInputWrapper = styled.div`
  .PhoneInput{background:${p=>p.active?'#1e1e1e':'#f7f8fa'};border:1px solid ${p=>p.active?'#333':'#ddd'};border-radius:8px;padding:7px 12px;display:flex;align-items:center;gap:8px;&:focus-within{border-color:rgba(128,128,128,0.6);}}
  .PhoneInputInput{background:transparent;border:none;outline:none;color:${p=>p.active?'#e0e0e0':'#222'};font-size:13px;width:100%;}
  .PhoneInputCountrySelect{background:${p=>p.active?'#1e1e1e':'#f7f8fa'};color:${p=>p.active?'#e0e0e0':'#333'};border:none;outline:none;cursor:pointer;font-size:12px;max-width:140px;}
  .PhoneInputCountryIcon{width:20px;height:14px;}
  .PhoneInputCountrySelectArrow{color:${p=>p.active?'#555':'#aaa'};margin-left:3px;}
`;

const SecurityNote = styled.div`margin-top:18px;padding:10px 14px;border-radius:10px;background:${p=>p.active?'rgba(255,200,0,0.06)':'rgba(255,200,0,0.1)'};border:1px solid ${p=>p.active?'rgba(255,200,0,0.15)':'rgba(255,200,0,0.3)'};color:${p=>p.active?'#a08030':'#7a6010'};font-size:12px;line-height:1.5;`;

const InfoCardGrid  = styled.div`display:grid;grid-template-columns:1fr 1fr;gap:10px;@media(max-width:600px){grid-template-columns:1fr;}`;
const InfoCardItem  = styled.div`display:flex;align-items:center;gap:12px;padding:12px 14px;border-radius:10px;background:${p=>p.active?'#1c1c1c':'#f7f8fa'};border:1px solid ${p=>p.active?'#272727':'#eee'};transition:background 0.2s;&:hover{background:${p=>p.active?'#222':'#f0f0f0'};}`;
const InfoCardIcon  = styled.div`font-size:20px;flex-shrink:0;`;
const InfoCardText  = styled.div`display:flex;flex-direction:column;gap:2px;min-width:0;`;
const InfoCardLabel = styled.span`font-size:10px;font-weight:700;text-transform:uppercase;letter-spacing:0.8px;color:${p=>p.active?'#555':'#aaa'};`;
const InfoCardValue = styled.span`font-size:13px;font-weight:600;color:${p=>p.active?'#e0e0e0':'#111'};white-space:nowrap;overflow:hidden;text-overflow:ellipsis;`;

/* Content grid */
const ContentGrid = styled.div`display:grid;grid-template-columns:repeat(4,1fr);gap:14px;@media(max-width:1100px){grid-template-columns:repeat(3,1fr);}@media(max-width:768px){grid-template-columns:repeat(2,1fr);}@media(max-width:480px){grid-template-columns:1fr;}`;

const ContentCard = styled.div`
  background:${p=>p.active?'#1a1a1a':'#f7f8fa'};
  border:1px solid ${p=>p.active?'#252525':'#eee'};
  border-radius:12px;overflow:hidden;
  display:flex;flex-direction:column;
  transition:transform 0.2s,box-shadow 0.2s;
  &:hover{transform:translateY(-3px);box-shadow:0 8px 24px ${p=>p.active?'rgba(0,0,0,0.5)':'rgba(0,0,0,0.12)'};}
`;

const Preview = styled.div`
  width:100%;height:145px;
  background:${p=>p.active?'#111':'#eee'};
  display:flex;align-items:center;justify-content:center;
  overflow:hidden;cursor:pointer;flex-shrink:0;
`;
const PreviewImg         = styled.img`width:100%;height:100%;object-fit:cover;`;
const PreviewPlaceholder = styled.div`font-size:34px;color:${p=>p.active?'#333':'#ccc'};`;
const PreviewIframe      = styled.iframe`width:100%;height:145px;border:none;`;
const FileIconWrap       = styled.div`display:flex;flex-direction:column;align-items:center;gap:5px;cursor:pointer;&:hover{opacity:0.8;}`;
const FileExt            = styled.span`font-size:11px;font-weight:700;color:${p=>p.active?'#888':'#999'};letter-spacing:1px;`;

const ContentMeta = styled.div`
  padding:10px 11px 11px;
  background:${p=>p.active?'#141414':'#fff'};
  display:flex;flex-direction:column;gap:5px;flex:1;
`;

const ContentTitle = styled.p`
  margin:0;font-size:13px;font-weight:600;
  color:${p=>p.active?'#e0e0e0':'#222'};
  white-space:nowrap;overflow:hidden;text-overflow:ellipsis;
  cursor:pointer;transition:color 0.2s;
`;

const ContentFooter = styled.div`display:flex;align-items:center;justify-content:space-between;`;
const TypePill = styled.span`font-size:10px;padding:2px 7px;border-radius:20px;background:${p=>p.type==='imagen'?'rgba(59,130,246,0.1)':p.type==='video'?'rgba(239,68,68,0.1)':'rgba(34,197,94,0.1)'};color:${p=>p.type==='imagen'?'#60a5fa':p.type==='video'?'#f87171':'#4ade80'};`;
const DateText = styled.span`font-size:10px;color:${p=>p.active?'#444':'#bbb'};`;

const ActionRow = styled.div`display:flex;align-items:center;justify-content:space-between;padding-top:2px;`;

const PricePill = styled.span`
  font-size:12px;font-weight:700;color:#f59e0b;
  background:rgba(245,158,11,0.1);border:1px solid rgba(245,158,11,0.3);
  padding:2px 9px;border-radius:20px;
`;

const BtnGroup  = styled.div`display:flex;align-items:center;gap:4px;`;

const DeleteBtn = styled.button`
  width:27px;height:27px;border-radius:7px;
  background:rgba(220,53,69,0.12);border:1px solid rgba(220,53,69,0.35);
  color:#f87171;font-size:11px;cursor:pointer;
  display:flex;align-items:center;justify-content:center;
  transition:all 0.2s;
  &:hover{background:rgba(220,53,69,0.25);transform:scale(1.08);}
`;

const EmptyMsg  = styled.p`text-align:center;color:${p=>p.active?'#444':'#bbb'};font-size:14px;padding:30px 0;`;
const Pagination= styled.div`display:flex;align-items:center;gap:5px;margin-top:18px;flex-wrap:wrap;`;
const PageBtn   = styled.button`min-width:30px;height:30px;border-radius:7px;font-size:12px;cursor:${p=>p.disabled?'not-allowed':'pointer'};opacity:${p=>p.disabled?0.4:1};border:1px solid ${p=>p.isCurrent?'rgba(128,128,128,0.8)':p.active?'#222':'#e0e0e0'};background:${p=>p.isCurrent?'rgba(128,128,128,0.2)':'transparent'};color:${p=>p.isCurrent?(p.active?'#fff':'#111'):p.active?'#666':'#888'};font-weight:${p=>p.isCurrent?700:'normal'};transition:all 0.2s;&:hover:not(:disabled){border-color:rgba(128,128,128,0.5);background:rgba(128,128,128,0.1);}`;
const PageInfo  = styled.span`font-size:11px;color:${p=>p.active?'#444':'#bbb'};margin-left:6px;`;

/* ── MultiSelect Dropdown ── */
const DropTrigger = styled.button`
  width:27px;height:27px;border-radius:7px;position:relative;
  background:${p => p.hasItems
    ? (p.color === '#60a5fa' ? 'rgba(96,165,250,0.25)' : 'rgba(248,113,113,0.25)')
    : (p.color === '#60a5fa' ? 'rgba(96,165,250,0.1)'  : 'rgba(248,113,113,0.1)')};
  border:1px solid ${p=>p.color==='#60a5fa'?'rgba(96,165,250,0.4)':'rgba(248,113,113,0.4)'};
  font-size:12px;cursor:pointer;
  display:flex;align-items:center;justify-content:center;
  transition:all 0.2s;
  &:hover{transform:scale(1.08);opacity:0.85;}
`;

const DropBadge = styled.span`
  position:absolute;top:-5px;right:-5px;
  background:${p=>p.color};color:#fff;
  font-size:9px;font-weight:700;
  min-width:14px;height:14px;border-radius:7px;
  display:flex;align-items:center;justify-content:center;padding:0 2px;
`;

/* Panel con position:fixed para no quedar cortado */
const DropPortal = styled.div`
  position:fixed;
  width:220px;
  background:${p=>p.isDark?'#1e1e1e':'#fff'};
  border:1px solid ${p=>p.isDark?'#333':'#ddd'};
  border-radius:12px;
  box-shadow:0 8px 32px rgba(0,0,0,0.35);
  z-index:9999;
  overflow:hidden;
  animation:dropIn 0.15s ease;
  @keyframes dropIn{from{opacity:0;transform:translateY(4px);}to{opacity:1;transform:translateY(0);}}
`;

const DropHeader = styled.div`padding:10px 12px 8px;border-bottom:1px solid ${p=>p.isDark?'#2a2a2a':'#f0f0f0'};`;
const DropTitle  = styled.div`font-size:10px;font-weight:700;text-transform:uppercase;letter-spacing:0.8px;color:${p=>p.isDark?'#555':'#aaa'};margin-bottom:6px;`;
const DropSearch = styled.input`
  width:100%;box-sizing:border-box;
  background:${p=>p.isDark?'#2a2a2a':'#f5f5f5'};
  border:1px solid ${p=>p.isDark?'#3a3a3a':'#e0e0e0'};
  border-radius:6px;padding:5px 8px;font-size:12px;
  color:${p=>p.isDark?'#e0e0e0':'#222'};outline:none;
  &::placeholder{color:${p=>p.isDark?'#555':'#bbb'};}
`;
const DropList  = styled.div`max-height:190px;overflow-y:auto;&::-webkit-scrollbar{width:3px;}&::-webkit-scrollbar-thumb{background:#444;border-radius:2px;}`;
const DropEmpty = styled.div`padding:14px;text-align:center;font-size:12px;color:${p=>p.isDark?'#444':'#bbb'};`;

const DropItem = styled.div`
  display:flex;align-items:center;gap:8px;padding:7px 12px;cursor:pointer;
  background:${p=>p.selected
    ? (p.color==='#60a5fa'?'rgba(96,165,250,0.1)':'rgba(248,113,113,0.1)')
    : 'transparent'};
  transition:background 0.12s;
  &:hover{background:${p=>p.isDark?'#2a2a2a':'#f5f5f5'};}
`;
const DropAvatar   = styled.div`width:22px;height:22px;border-radius:50%;flex-shrink:0;background:${p=>p.color==='#60a5fa'?'rgba(96,165,250,0.18)':'rgba(248,113,113,0.18)'};color:${p=>p.color};display:flex;align-items:center;justify-content:center;font-size:10px;font-weight:700;`;
const DropItemName = styled.span`flex:1;font-size:12px;font-weight:500;color:${p=>p.isDark?'#d0d0d0':'#333'};white-space:nowrap;overflow:hidden;text-overflow:ellipsis;`;
const DropCheck    = styled.div`width:15px;height:15px;border-radius:4px;flex-shrink:0;border:1px solid ${p=>p.selected?p.color:(p.isDark?'#3a3a3a':'#ddd')};background:${p=>p.selected?p.color:'transparent'};color:#fff;font-size:9px;font-weight:700;display:flex;align-items:center;justify-content:center;transition:all 0.12s;`;

const EditBtn = styled.button`
  width:27px;height:27px;border-radius:7px;
  background:rgba(79,70,229,0.12);border:1px solid #ffc107;
  color:#ffc107;font-size:11px;cursor:pointer;
  display:flex;align-items:center;justify-content:center;
  transition:all 0.2s;
  &:hover{background:#ffc107;transform:scale(1.08);}
`;
const LogoDropZone = styled.div`
  position: relative;
  width: 100px;
  height: 100px;
  border-radius: 50%;
  border: 2px dashed ${p => p.dragOver ? 'rgba(79,70,229,0.75)' : p.active ? '#2a2a2a' : '#ddd'};
  background: ${p => p.dragOver ? 'rgba(79,70,229,0.05)' : p.active ? '#1a1a1a' : '#f7f8fa'};
  display: flex; align-items: center; justify-content: center;
  cursor: pointer; overflow: hidden; transition: all 0.2s;
  &:hover { border-color: rgba(79,70,229,0.5); }
`;

const LogoPreviewImg = styled.img`
  width: 100%; height: 100%; object-fit: cover;
`;

const LogoDropContent = styled.div`
  display: flex; flex-direction: column; align-items: center; gap: 4px; padding: 8px;
`;

const LogoDropIcon = styled.div`
  font-size: 20px; color: ${p => p.active ? '#666' : '#ccc'};
`;

const LogoDropText = styled.p`
  margin: 0; font-size: 9px; text-align: center; line-height: 1.3;
  color: ${p => p.active ? '#666' : '#999'};
`;

const LogoHiddenInput = styled.input`
  position: absolute; inset: 0; opacity: 0; cursor: pointer; width: 100%; height: 100%;
`;

const StoreEditLayout = styled.div`
  display: flex;
  align-items: center;
  gap: 28px;
  @media(max-width: 600px) {
    flex-direction: column;
    align-items: center;
  }
`;

const StoreLogoCol = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
`;

const StoreFieldsCol = styled.div`
  display: flex;
  flex-direction: column;
  gap: 14px;
  flex: 1;
  min-width: 0;
`;


const StoreLogoImg = styled.img`
  width: 90px;
  height: 90px;
  border-radius: 50%;
  object-fit: cover;
  border: 1px solid rgba(128,128,128,0.2);
`;

const StoreLogoPlaceholder = styled.div`
  width: 90px;
  height: 90px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 28px;
  color: ${p => p.active ? '#444' : '#ccc'};
  background: ${p => p.active ? '#1c1c1c' : '#f0f0f0'};
  border: 1px solid ${p => p.active ? '#272727' : '#eee'};
`;

const UrlCardItem = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 14px;
  border-radius: 10px;
  background: ${p => p.active ? '#1c1c1c' : '#f7f8fa'};
  border: 1px solid ${p => p.active ? '#272727' : '#eee'};
  transition: background 0.2s;
  &:hover { background: ${p => p.active ? '#222' : '#f0f0f0'}; }
`;

const CopyBtn = styled.button`
  display: flex;
  align-items: center;
  gap: 5px;
  flex-shrink: 0;
  padding: 6px 12px;
  border-radius: 6px;
  font-size: 11px;
  font-weight: 600;
  cursor: pointer;
  background: ${p => p.active ? 'rgba(255,255,255,0.06)' : '#fff'};
  border: 1px solid ${p => p.active ? '#333' : '#ddd'};
  color: ${p => p.active ? '#ccc' : '#555'};
  transition: all 0.2s;
  &:hover {
    border-color: rgba(79,70,229,0.5);
    color: ${p => p.active ? '#818cf8' : '#4f46e5'};
    background: rgba(79,70,229,0.08);
  }
`;

const TwoColSmall = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
  @media(max-width: 600px) {
    grid-template-columns: 1fr;
  }
`;

const FieldError = styled.span`
  font-size: 11px;
  font-weight: 600;
  color: #f87171;
  line-height: 1.3;
`;

const UrlPreview = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  padding: 8px 12px;
  min-height: 35px;
  box-sizing: border-box;
  border-radius: 8px;
  font-size: 13px;
  word-break: break-all;
  background: ${p => p.active ? '#1a1a1a' : '#f0f1f4'};
  border: 1px dashed ${p => p.hasError ? '#f87171' : p.active ? '#333' : '#ccc'};
  cursor: default;
`;

const UrlBase = styled.span`
  color: ${p => p.active ? '#666' : '#999'};
`;

const UrlSlug = styled.span`
  font-weight: 700;
  color: ${p => p.active ? '#818cf8' : '#4f46e5'};
`;

export default UserProfile;